import React, { useState, useEffect } from 'react';
import { useNavigate, Link as RouteLink } from 'react-router-dom';
import {
  Box,
  VStack,
  HStack,
  Text,
  Alert,
  AlertIcon,
  AlertDescription,
  Divider,
  useColorModeValue,
  Container,
  useToast,
  Badge,
  Tooltip,
  Image,
} from '@chakra-ui/react';
import {
  FiUser,
  FiLock,
  FiEye,
  FiEyeOff,
  FiLogIn,
  FiHelpCircle,
} from 'react-icons/fi';

import { Card, Button, FormField } from '@grcs/components';
import { SEO } from '@grcs/components/SEO';

import { useUserOperations } from '@grcs/hook/useDatabase';
import { useLocalStorage } from '@grcs/hook/useLocalStorage';
import { comparePassword } from '@grcs/utils/auth/password';
import { EventCategory, EventStore } from '@grcs/analytics/EventStore';
import { UserRole } from '@grcs/types/UserRole';
import { normalizedLanguage } from '@grcs/utils/language/normalizedLanguage';
import csrf_logo from '@grcs/images/csrf_logo.png';
import background_image from '@grcs/images/grcs_homepage.jpg';

interface StudentLoginFormData {
  username: string;
  password: string;
}

interface LoginError {
  field?: 'username' | 'password' | 'general';
  message: string;
}

export const StudentLoginEnhanced: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const userOps = useUserOperations();
  const [, setUser] = useLocalStorage('user', {});
  const [, setUserLanguage] = useLocalStorage('user_language', 'en');
  const [storedAuthLanguage] = useLocalStorage('auth_language', 'en');
  const authLanguage = storedAuthLanguage as 'en' | 'zh';

  // Form state
  const [formData, setFormData] = useState<StudentLoginFormData>({
    username: '',
    password: '',
  });

  // UI state
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<LoginError[]>([]);
  // const [rememberMe, setRememberMe] = useState(false);

  // Theme colors
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const overlayBg = useColorModeValue('blackAlpha.600', 'blackAlpha.800');
  const isChinese = authLanguage === 'zh';
  const copy = {
    title: isChinese ? '学生登录' : 'Student Login',
    demo: isChinese ? '试用演示账号' : 'Try Demo Account',
    usernameLabel: isChinese ? '用户名' : 'Username',
    usernamePlaceholder: isChinese ? '输入你的用户名' : 'Enter your username',
    usernameTooltip: isChinese
      ? '你的用户名由老师提供'
      : 'Your username is provided by your teacher',
    passwordLabel: isChinese ? '密码' : 'Password',
    passwordHelper: isChinese
      ? '通常为出生日期（YYYYMMDD）'
      : 'Usually your date of birth (YYYYMMDD)',
    passwordPlaceholder: isChinese ? '输入你的密码' : 'Enter your password',
    signingIn: isChinese ? '登录中...' : 'Signing in...',
    submit: isChinese ? '开始学习' : 'Start Learning! 🚀',
    teacherLogin: isChinese ? '教师登录' : 'Teacher Login',
    footer: '© 2024 泓河开卷™ by Grand River Chinese School',
    usernameRequired: isChinese ? '请输入用户名' : 'Username is required',
    usernameTooShort: isChinese
      ? '用户名至少需要 3 个字符'
      : 'Username must be at least 3 characters',
    passwordRequired: isChinese ? '请输入密码' : 'Password is required',
    usernameMissing: isChinese
      ? '未找到该用户名，请检查后重试。'
      : 'Username not found. Please check your username.',
    passwordIncorrect: isChinese
      ? '密码不正确，请重试。'
      : 'Incorrect password. Please try again.',
    welcomeTitle: isChinese ? '欢迎回来！' : 'Welcome back!',
    welcomeDescription: (name: string) =>
      isChinese
        ? `你好 ${name}！准备开始今天的学习吧！`
        : `Hello ${name}! Ready to learn today?`,
    generalError: isChinese
      ? '发生了一些问题。请重试；如果问题持续存在，请联系支持人员。'
      : 'Something went wrong. Please try again or contact support if the problem persists.',
  };

  // Auto-fill demo credentials for development
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      setFormData({
        username: 'bo_zhaobocong@hotmail.com',
        password: '20240402',
      });
    }
  }, []);

  // Validation
  const validateForm = (): boolean => {
    const newErrors: LoginError[] = [];
    const trimmedUsername = formData.username.trim();

    if (!trimmedUsername) {
      newErrors.push({ field: 'username', message: copy.usernameRequired });
    } else if (trimmedUsername.length < 3) {
      newErrors.push({
        field: 'username',
        message: copy.usernameTooShort,
      });
    }

    if (!formData.password) {
      newErrors.push({ field: 'password', message: copy.passwordRequired });
    }

    setErrors(newErrors);
    return newErrors.length === 0;
  };

  const handleLogin = async () => {
    if (!validateForm()) return;

    setIsLoading(true);
    setErrors([]);

    try {
      // Trim username to remove any trailing/leading spaces
      const trimmedUsername = formData.username.trim();

      // Track login attempt
      EventStore.get().sendEvent('StudentLogin', EventCategory.CLICK);

      // Get user by username
      const userResult = await userOps.getUserByUsername(trimmedUsername);

      if (!userResult.data || userResult.data.length === 0) {
        setErrors([
          {
            field: 'username',
            message: copy.usernameMissing,
          },
        ]);
        return;
      }

      const studentData = userResult.data[0];

      // Verify password
      const isPasswordMatch = await comparePassword(
        formData.password,
        studentData.password,
      );

      if (!isPasswordMatch) {
        setErrors([
          {
            field: 'password',
            message: copy.passwordIncorrect,
          },
        ]);
        return;
      }

      let preferredLanguage = 'en';
      try {
        const languageResult = await userOps.getUserLanguageByType(
          UserRole.STUDENT,
        );
        preferredLanguage = normalizedLanguage(
          languageResult.data?.[0]?.language,
        );
      } catch (languageError) {
        console.warn(
          'Failed to load student language preference.',
          languageError,
        );
      }
      setUserLanguage(preferredLanguage);

      // Set user data in localStorage
      setUser({
        ...studentData,
        role: studentData.role,
        isLogin: true,
        error: '',
        email: studentData.email,
        password: '',
      });

      // Show success toast
      toast({
        title: copy.welcomeTitle,
        description: copy.welcomeDescription(
          studentData.first_name || studentData.full_name,
        ),
        status: 'success',
        duration: 4000,
        isClosable: true,
      });

      // Navigate to student dashboard
      navigate('/student', { replace: true });
    } catch (error: any) {
      console.error('Login error:', error);
      setErrors([
        {
          field: 'general',
          message: copy.generalError,
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (
    field: keyof StudentLoginFormData,
    value: string,
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear field-specific errors
    setErrors((prev) => prev.filter((error) => error.field !== field));
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !isLoading) {
      handleLogin();
    }
  };

  const getFieldError = (field: string) => {
    return errors.find((error) => error.field === field)?.message;
  };

  const generalError = errors.find((error) => error.field === 'general')
    ?.message;

  return (
    <>
      <SEO
        title="Student Login"
        description="Grand River Chinese School student login portal. Access your homework and Chinese reading comprehension assignments."
        keywords="student login, Chinese homework, reading assignments, Grand River Chinese School"
      />
      <Box
        h="100dvh"
        position="relative"
        display="flex"
        flexDirection="column"
        overflow="hidden"
      >
        <Box
          h="95%"
          display="flex"
          justifyContent="center"
          backgroundImage={`url(${background_image})`}
          backgroundSize="cover"
          backgroundPosition="center"
        >
          <Container
            maxW="6xl"
            w={{ base: '80%', md: '60%', lg: '50%' }}
            h="100%"
            px={{ base: 4, md: 8 }}
            display="flex"
            flexDirection="column"
          >
            <Box
              flex="0 0 auto"
              minH={{ base: '120px', md: '20%' }}
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <VStack spacing={2} textAlign="center">
                <Box
                  display="flex"
                  flexDirection={{ base: 'column', md: 'row' }}
                  alignItems="center"
                  justifyContent="center"
                  gap={{ base: 3, md: 6 }}
                >
                  <Image
                    src={csrf_logo}
                    alt="School Logo"
                    h={{ base: '48px', md: '60px' }}
                  />
                  <Text
                    fontSize="4xl"
                    fontWeight="bold"
                    color="black.600"
                    display="inline-block"
                    whiteSpace="nowrap"
                    lineHeight="1"
                    flexShrink={0}
                  >
                    泓河开卷&trade;
                  </Text>
                </Box>
              </VStack>
            </Box>

            <Box
              flex="1"
              minH={0}
              display="flex"
              alignItems="flex-start"
              justifyContent="center"
            >
              <Card
                w="100%"
                maxH="100%"
                overflowY="auto"
                bg={cardBg}
                borderColor={borderColor}
                shadow="2xl"
                borderRadius="2xl"
                borderWidth="1px"
                px={{ base: 6, md: 10 }}
                py={{ base: 6, md: 8 }}
              >
                <VStack spacing={6} align="stretch">
                  <VStack spacing={2} textAlign="center">
                    <Text fontSize="2xl" fontWeight="bold" color="black.700">
                      {copy.title}
                    </Text>
                  </VStack>

                  {/* Demo Credentials Alert */}
                  {process.env.NODE_ENV === 'development' && (
                    <Alert status="success" borderRadius="lg" variant="subtle">
                      <AlertIcon />
                      <VStack align="start" spacing={1} fontSize="sm">
                        <AlertDescription fontWeight="semibold">
                          {copy.demo}
                        </AlertDescription>
                        <Text>
                          Username: bo_zhaobocong@hotmail.com | Password:
                          20240402
                        </Text>
                      </VStack>
                    </Alert>
                  )}

                  {/* General Error */}
                  {generalError && (
                    <Alert status="error" borderRadius="lg">
                      <AlertIcon />
                      <AlertDescription>{generalError}</AlertDescription>
                    </Alert>
                  )}

                  {/* Login Form */}
                  <VStack spacing={5} align="stretch">
                    <FormField
                      label={copy.usernameLabel}
                      required
                      error={getFieldError('username')}
                    >
                      <Box position="relative">
                        <input
                          type="text"
                          placeholder={copy.usernamePlaceholder}
                          value={formData.username}
                          onChange={(e) =>
                            handleInputChange('username', e.target.value)
                          }
                          onKeyPress={handleKeyPress}
                          disabled={isLoading}
                          style={{
                            paddingTop: '14px',
                            paddingBottom: '14px',
                            paddingLeft: '50px',
                            paddingRight: '50px',
                            width: '100%',
                            height: '52px',
                            border: `2px solid ${
                              getFieldError('username') ? 'red' : '#e2e8f0'
                            }`,
                            borderRadius: '12px',
                            fontSize: '16px',
                            lineHeight: '24px',
                            transition: 'all 0.2s',
                            outline: 'none',
                            boxSizing: 'border-box',
                          }}
                        />
                        <Box
                          position="absolute"
                          left="16px"
                          top="50%"
                          transform="translateY(-50%)"
                          color="blue.400"
                          zIndex="1"
                        >
                          <FiUser size={20} />
                        </Box>
                        <Tooltip label={copy.usernameTooltip} hasArrow>
                          <Box
                            position="absolute"
                            right="16px"
                            top="50%"
                            transform="translateY(-50%)"
                            color="gray.400"
                            cursor="help"
                            _hover={{ color: 'blue.400' }}
                          >
                            <FiHelpCircle />
                          </Box>
                        </Tooltip>
                      </Box>
                    </FormField>

                    <FormField
                      label={copy.passwordLabel}
                      required
                      error={getFieldError('password')}
                      helperText={copy.passwordHelper}
                    >
                      <Box position="relative">
                        <input
                          type={showPassword ? 'text' : 'password'}
                          placeholder={copy.passwordPlaceholder}
                          value={formData.password}
                          onChange={(e) =>
                            handleInputChange('password', e.target.value)
                          }
                          onKeyPress={handleKeyPress}
                          disabled={isLoading}
                          style={{
                            paddingTop: '14px',
                            paddingBottom: '14px',
                            paddingLeft: '50px',
                            paddingRight: '50px',
                            width: '100%',
                            height: '52px',
                            border: `2px solid ${
                              getFieldError('password') ? 'red' : '#e2e8f0'
                            }`,
                            borderRadius: '12px',
                            fontSize: '16px',
                            lineHeight: '24px',
                            transition: 'all 0.2s',
                            outline: 'none',
                            boxSizing: 'border-box',
                          }}
                        />
                        <Box
                          position="absolute"
                          left="16px"
                          top="50%"
                          transform="translateY(-50%)"
                          color="blue.400"
                          zIndex="1"
                        >
                          <FiLock size={20} />
                        </Box>
                        <Box
                          position="absolute"
                          right="16px"
                          top="50%"
                          transform="translateY(-50%)"
                          cursor="pointer"
                          color="gray.400"
                          _hover={{ color: 'blue.400' }}
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          {showPassword ? (
                            <FiEyeOff size={20} />
                          ) : (
                            <FiEye size={20} />
                          )}
                        </Box>
                      </Box>
                    </FormField>

                    {/* Remember Me */}
                    {/* <HStack justify="space-between">
                      <HStack spacing={3}>
                      <input
                        type="checkbox"
                        id="remember-student"
                        checked={rememberMe}
                        onChange={(e) => setRememberMe(e.target.checked)}
                        style={{
                          accentColor: '#3182ce',
                        }}
                      />
                      <Text fontSize="sm" color="gray.600">
                        Remember me on this device
                      </Text>
                    </HStack>
                  </HStack> */}

                    {/* Login Button */}
                    <Button
                      leftIcon={<FiLogIn />}
                      onClick={handleLogin}
                      isLoading={isLoading}
                      loadingText={copy.signingIn}
                      isDisabled={!formData.username || !formData.password}
                      size="lg"
                      width="100%"
                      colorScheme="blue"
                      bgGradient="linear(to-r, blue.400, blue.500)"
                      _hover={{
                        bgGradient: 'linear(to-r, blue.500, blue.600)',
                        transform: 'translateY(-1px)',
                        shadow: 'lg',
                      }}
                      borderRadius="12px"
                      py={6}
                    >
                      {copy.submit}
                    </Button>
                  </VStack>

                  <Divider />

                  {/* Additional Information */}
                  <VStack spacing={4} fontSize="sm" textAlign="center">
                    <HStack spacing={4} justify="center">
                      <RouteLink to="/auth/admin-login">
                        <Text color="blue.500" _hover={{ color: 'blue.600' }}>
                          {copy.teacherLogin}
                        </Text>
                      </RouteLink>
                    </HStack>
                  </VStack>
                </VStack>
              </Card>
            </Box>
          </Container>
        </Box>

        <Box
          h="10%"
          bg={overlayBg}
          borderTop="1px solid"
          borderTopColor="whiteAlpha.200"
          display="flex"
          alignItems="center"
        >
          <Container maxW="6xl" px={{ base: 4, md: 8 }}>
            <VStack spacing={2} textAlign="center">
              <Box
                display="flex"
                flexDirection={{ base: 'column', md: 'row' }}
                alignItems="center"
                justifyContent="center"
                gap={2}
              >
                <Image
                  src={csrf_logo}
                  alt="School Logo"
                  h={{ base: '20px', md: '24px' }}
                />
                <Text
                  color="white"
                  fontWeight="bold"
                  fontSize={{ base: 'sm', md: 'base' }}
                >
                  泓河开卷&trade;
                </Text>
              </Box>
              <VStack spacing={1}>
                <Text
                  color="whiteAlpha.900"
                  fontSize={{ base: 'xs', md: 'sm' }}
                  fontWeight="500"
                >
                  {copy.footer}
                </Text>
              </VStack>
            </VStack>
          </Container>
        </Box>
      </Box>
    </>
  );
};
